import { tablesManualChapter, type ManualBlock } from '@matix/types';
import { useUI } from '../../store/ui';
import { useLang } from '../../lib/language';
import { XIcon } from '../../components/icons';

/**
 * "How tables work" — the user manual's Dine-in tables chapter, shown in the
 * till itself (the cart's link opens it). Same content as the admin manual's
 * /manual/tables, from @matix/types, so the cashier never leaves the till and
 * needs no admin sign-in. Follows the till's language.
 */
export function TablesManualModal() {
  const isOpen = useUI((s) => s.openModal === 'tablesManual');
  const closeModal = useUI((s) => s.closeModal);
  const { t: tr, lang } = useLang();
  if (!isOpen) return null;

  const ch = tablesManualChapter;
  const pick = (p: { en: string; ar: string }): string => (lang === 'ar' ? p.ar : p.en);
  const list = (b: { en: string[]; ar: string[] }): string[] => (lang === 'ar' ? b.ar : b.en);

  const box = { borderRadius: 8, padding: '8px 12px', margin: '8px 0', fontSize: 13 } as const;

  function renderBlock(b: ManualBlock, i: number) {
    switch (b.kind) {
      case 'para':
        return <p key={i} style={{ margin: '8px 0', lineHeight: 1.6 }}>{pick(b)}</p>;
      case 'note':
        return <div key={i} style={{ ...box, background: 'var(--bg-muted, #f1f5f9)' }}>{pick(b)}</div>;
      case 'warn':
        return <div key={i} style={{ ...box, background: '#fef3c7', color: '#92400e' }}>{pick(b)}</div>;
      case 'steps':
        return <ol key={i} style={{ margin: '8px 0', paddingInlineStart: 22, lineHeight: 1.6 }}>{list(b).map((s, j) => <li key={j}>{s}</li>)}</ol>;
      case 'bullets':
        return <ul key={i} style={{ margin: '8px 0', paddingInlineStart: 22, lineHeight: 1.6 }}>{list(b).map((s, j) => <li key={j}>{s}</li>)}</ul>;
      case 'fields':
        return (
          <table key={i} style={{ width: '100%', borderCollapse: 'collapse', margin: '8px 0', fontSize: 13 }}>
            <tbody>
              {b.rows.map((r, j) => (
                <tr key={j} style={{ borderTop: '1px solid var(--border, #e5e7eb)' }}>
                  <td style={{ padding: '6px 8px', fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{pick(r.label)}</td>
                  <td style={{ padding: '6px 8px', lineHeight: 1.5 }}>{pick(r.body)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case 'example':
        return (
          <div key={i} style={{ ...box, background: 'var(--bg-muted, #f1f5f9)' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{pick(b.title)}</div>
            <pre style={{ margin: 0, fontFamily: 'ui-monospace, Consolas, monospace', whiteSpace: 'pre-wrap' }}>
              {b.lines.map((l) => pick(l)).join('\n')}
            </pre>
          </div>
        );
      case 'faq':
        return (
          <div key={i}>
            {b.rows.map((r, j) => (
              <details key={j} style={{ margin: '6px 0' }}>
                <summary style={{ fontWeight: 600, cursor: 'pointer' }}>{pick(r.q)}</summary>
                <div style={{ padding: '4px 0 4px 16px', lineHeight: 1.6 }}>{pick(r.a)}</div>
              </details>
            ))}
          </div>
        );
    }
  }

  return (
    <div className="modal-overlay show" onClick={closeModal}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        dir={lang === 'ar' ? 'rtl' : 'ltr'}
        style={{ maxWidth: 760, width: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="modal-header-title">
            <div className="modal-header-name">{pick(ch.title)}</div>
            <div className="modal-header-name-ar">{pick(ch.summary)}</div>
          </div>
          <button type="button" className="modal-close" onClick={closeModal} aria-label={tr({ en: 'Close', ar: 'إغلاق' })}>
            <XIcon size={18} />
          </button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {ch.sections.map((s) => (
            <section key={s.id} style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: '12px 0 4px' }}>{pick(s.title)}</h3>
              {s.blocks.map(renderBlock)}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
